import { useDispatch, useSelector } from "react-redux";
import {
  ISubscription,
  ISubscriptionFeature,
} from "../../Models/ISubscription";
import "./StorePage.scss";
import { GlobalState } from "../../Redux/RootReducer";
import Tr from "../../Utils/Translations/Translations";
import {
  Checkbox,
  DefaultButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
} from "@fluentui/react";
import SmartModal, {
  DismissModal,
  SummonModal,
} from "../../Components/SmartModal/SmartModal";
import { useState } from "react";
import ApiService from "../../Services/ApiService";
import { IAPIResponse } from "../../Services/AjaxService";
import { ToastMessage, ToastMessageUnique } from "../../Utils/UIMessages";
import { GenericActions } from "../../Redux/Generic/GenericAction";
import { IVoucher } from "../../Models/IVoucher";
import BetterTextField from "../../Components/BetterTextField/BetterTextField";
import { mobileCheck } from "../../App";
import SuperSpinner from "../../Components/SuperSpinner/SuperSpinner";
import ImageAssetLoader from "../../Components/ImageAssetLoader/ImageAssetLoader";
import BetterRadioButtons from "../../Components/BetterRadioButtons/BetterRadioButtons";

interface ISubscriptionPurchasePreviewProps {
  subscription: ISubscription | undefined;
  onBack?: () => void;
  paymentDone: (data: {
    voucher: IVoucher | undefined;
    payYearly: boolean;
  }) => void;
}

export const getFeatureAmountLabel = (feature: ISubscriptionFeature) => {
  let extra: JSX.Element = <div>{feature.FeatureAmount}</div>;
  if (["QUERY_BOT", "EVENT_ERROR_DETECTION"].includes(feature.FeatureCode)) {
    extra = (
      <div>
        <div>{feature.FeatureAmount}</div>
        <div
          style={mobileCheck() ? { fontSize: "0.6em" } : { fontSize: "0.8em" }}
        >
          {Tr.Translate("subscription", "per_month_calls")}
        </div>
      </div>
    );
  }
  return <div>{extra}</div>;
};

export const SubscriptionFeatureListComponent = (props: {
  subscription: ISubscription | undefined;
}) => {
  const subscriptionFeatures: ISubscriptionFeature[] = useSelector(
    (state: GlobalState) => state.generic.subscriptionFeatures
  );
  return (
    <div>
      <div className="subscription-includes">
        {Tr.Translate("subscription", "subscription_includes")}
      </div>
      {subscriptionFeatures.length === 0 && (
        <div style={{ padding: "1em" }}>
          <SuperSpinner />
        </div>
      )}
      {subscriptionFeatures.length > 0 && props.subscription && (
        <div>
          {subscriptionFeatures
            .filter(
              (x: ISubscriptionFeature) =>
                x.SubscriptionId === props.subscription?.Id &&
                x.FeatureAmount > 0
            )
            .map((x: ISubscriptionFeature, i: number) => (
              <div key={i} className="subscription-includes-line">
                <div>
                  <div className="subscription-includes-title">
                    {Tr.Translate("subscription", x.FeatureLabel)}
                  </div>
                  <div className="subscription-includes-description">
                    {Tr.Translate("subscription", x.FeatureDescription)}
                  </div>
                </div>
                <div className={"subscription-includes-amounts"}>
                  <div>{Tr.Translate("subscription", "amount")}</div>
                  <strong className="subscription-includes-amounts-label">
                    {getFeatureAmountLabel(x)}
                  </strong>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const SubscriptionPurchasePreview = (
  props: ISubscriptionPurchasePreviewProps
) => {
  const [payYearly, setPayYearly] = useState<boolean>(false);
  const [cupon, setCupon] = useState<string>("");
  const [voucher, setVoucher] = useState<IVoucher[]>([]);
  const [loadingVoucher, setLoadingVoucher] = useState<boolean>(false);

  const loadVoucher = () => {
    setLoadingVoucher(true);
    ApiService.StoreController.TestVoucherEffect(
      cupon,
      (response: IAPIResponse) => {
        if (response.error === null) {
          if (response.parsed.length === 0)
            ToastMessage("warning", Tr.Translate("language", "bad_cupon"));
          setVoucher(response.parsed);
        }
        setLoadingVoucher(false);
      }
    );
  };

  const purchaseFinalCost = payYearly
    ? props.subscription?.ExposedPriceYearly ?? 0
    : props.subscription?.ExposedPrice ?? 0;
  const couponCostResult =
    voucher.length > 0
      ? (purchaseFinalCost * (100 - voucher[0].Discount)) / 100
      : -1;
  const purchaseAppliedCost =
    couponCostResult !== -1 ? couponCostResult : purchaseFinalCost;

  const computeAnnualDiscount = (monthly: number, yearly: number) => {
    if (monthly === 0) return "0";
    return (100 - (yearly / (monthly * 12)) * 100).toFixed(0);
  };

  return (
    <div className="subscription-recap-wrap">
      <div className="subscription-recap-title">
        <DefaultButton
          iconProps={{ iconName: "Back" }}
          text={Tr.Translate("language", "back")}
          onClick={props.onBack}
        />
        {Tr.Translate("subscription", props.subscription?.SubscriptionName)}
      </div>
      <div className="subscription-recap-description">
        {Tr.Translate(
          "subscription",
          props.subscription?.SubscriptionDescription
        )}
      </div>
      <SubscriptionFeatureListComponent subscription={props.subscription} />

      <div
        className="subscription-coupon-commands"
        style={mobileCheck() ? { flexDirection: "column" } : {}}
      >
        <div className="coupon-line-wrap">
          {loadingVoucher ? (
            <SuperSpinner />
          ) : (
            <div style={{ display: "flex", gap: "0.5em", alignItems: "end" }}>
              <BetterTextField
                maxLength={50}
                value={cupon}
                onChange={(e, t) => t !== undefined && setCupon(t)}
                placeholder={Tr.Translate("language", "voucher_code")}
              />
              <PrimaryButton
                disabled={cupon.length < 5 || loadingVoucher}
                iconProps={{ iconName: "AutoEnhanceOn" }}
                onClick={loadVoucher}
              />
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: "1em" }}>
        <BetterRadioButtons
          fieldName="paymentType"
          defaultChecked={payYearly ? "yearly" : "monthly"}
          options={[
            {
              key: "monthly",
              text: `${Tr.Translate("language", "pay_monthly")} ${
                props.subscription?.ExposedPrice ?? 0
              }€`,
            },
            {
              key: "yearly",
              text: `${Tr.Translate("language", "pay_yearly")} ${
                props.subscription?.ExposedPriceYearly ?? 0
              }€ (-${computeAnnualDiscount(
                props.subscription?.ExposedPrice ?? 0,
                props.subscription?.ExposedPriceYearly ?? 0
              )}%)`,
            },
          ]}
          onChange={(c: string) => setPayYearly(c === "yearly")}
        />
      </div>

      <div className="subscription-recap-payment-price">
        <div
          style={voucher.length > 0 ? { textDecoration: "line-through" } : {}}
        >
          <pre>Tot: {purchaseFinalCost} €</pre>
        </div>
        {voucher.length > 0 && (
          <div>
            <pre>
              {cupon}: {purchaseAppliedCost} €
            </pre>
          </div>
        )}
      </div>

      <hr />
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          padding: "0.5em",
        }}
      >
        <PrimaryButton
          onClick={() => props.paymentDone({ voucher: voucher[0], payYearly })}
          text={Tr.Translate("language", "go_to_payment")}
          iconProps={{ iconName: "PaymentCard" }}
        />
      </div>
    </div>
  );
};

const StorePage = (props: { onlyPreview?: boolean }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageType, setPageType] = useState<"store" | "purchase" | "void">(
    "store"
  );
  const [activeSubscription, setActiveSubscription] = useState<ISubscription>();
  const subscriptions: ISubscription[] = useSelector(
    (state: GlobalState) => state.generic.subscriptions
  );

  const GetPurchases = () => {
    ApiService.StoreController.GetPurchases(
      undefined,
      (response: IAPIResponse) => {
        if (response.error === null)
          dispatch(GenericActions.setPurchases(response.parsed));
      }
    );
  };

  const initiateStripeCheckout = (
    voucher: IVoucher | undefined,
    payYearly: boolean
  ) => {
    if (activeSubscription) {
      setLoading(true);
      const payload = {
        SubscriptionId: activeSubscription.Id!,
        TimeSpan: payYearly ? "year" : "month",
        IsSubscription: true,
        VoucherCode: voucher ? voucher.VoucherCode : undefined,
        RequesTrial: false,
        ExternalSystemOrderId: payYearly
          ? activeSubscription.ExternalPriceYearlyId
          : activeSubscription.ExternalPriceMonthlyId,
      };

      ApiService.StoreController.NewPurchase(
        payload,
        async (response: IAPIResponse) => {
          if (response.error === null && response.parsed.checkoutUrl) {
            window.location.href = response.parsed.checkoutUrl;
          } else {
            ToastMessage("error", response.error ?? "Payment Error");
            setLoading(false);
          }
        }
      );
    }
  };

  const startFreeTrial = () => {
    if (activeSubscription) {
      setLoading(true);
      ApiService.StoreController.NewPurchase(
        {
          SubscriptionId: activeSubscription.Id!,
          TimeSpan: "month",
          IsSubscription: true,
          RequesTrial: true,
          ExternalSystemOrderId: activeSubscription.ExternalPriceMonthlyId,
        },
        (response: IAPIResponse) => {
          if (response.error === null) {
            GetPurchases();
            ToastMessage("success", Tr.Translate("language", "trial_started"));
            DismissModal("storeModal");
          } else if (response.raw.status === 422) {
            ToastMessageUnique(
              "error",
              Tr.Translate("language", "already_have_trial_on_this")
            );
          }
          setLoading(false);
          DismissModal("confirmTrialAction");
        }
      );
    }
  };

  return (
    <div className="subscription-main-wrap-outer">
      <SmartModal
        title={Tr.Translate("language", "purchase_with_trial")}
        modalUniqueId="confirmTrialAction"
        buttons={[
          {
            text: Tr.Translate("language", "cancel"),
            disabled: loading,
            onClick: () => DismissModal("confirmTrialAction"),
          },
          {
            text: Tr.Translate("language", "accept"),
            disabled: loading,
            onClick: startFreeTrial,
          },
        ]}
        content={
          loading ? (
            <SuperSpinner />
          ) : (
            <MessageBar messageBarType={MessageBarType.warning}>
              {Tr.Translate("language", "purchase_with_trial_about_to_start")}
            </MessageBar>
          )
        }
      />

      {subscriptions.length === 0 ? (
        <SuperSpinner />
      ) : (
        <div>
          {pageType === "purchase" &&
            (loading ? (
              <div className="center-spinner-store">
                <SuperSpinner />
                <div>
                  {Tr.Translate("language", "redirecting_to_secure_payment")}
                </div>
              </div>
            ) : (
              <SubscriptionPurchasePreview
                subscription={activeSubscription}
                onBack={() => setPageType("store")}
                paymentDone={(d) =>
                  initiateStripeCheckout(d.voucher, d.payYearly)
                }
              />
            ))}
          {pageType === "store" &&
            [...subscriptions]
              .sort((a, b) => a.ExposedPrice - b.ExposedPrice)
              .filter(
                (x) =>
                  !x.DevelopItem || window.location.origin.includes("localhost")
              )
              .map((x, i) => (
                <div
                  key={i}
                  className={
                    "subscription-main-wrap " +
                    (mobileCheck() ? "subscription-main-wrap-mobile" : "")
                  }
                >
                  <div className="subscription-name-desc-wrap">
                    <div className="subscription-name">
                      {Tr.Translate("subscription", x.SubscriptionName)}
                    </div>
                    <div className="subscription-description">
                      {Tr.Translate("subscription", x.SubscriptionDescription)}
                    </div>
                    {!props.onlyPreview && (
                      <div className="subscription-button-section">
                        <PrimaryButton
                          disabled={loading}
                          onClick={() => {
                            setActiveSubscription(x);
                            setPageType("purchase");
                          }}
                          iconProps={{ iconName: "Diamond" }}
                          text={Tr.Translate("language", "purchase")}
                        />
                        {x.TrialDays > 0 && (
                          <PrimaryButton
                            onClick={() => {
                              setActiveSubscription(x);
                              SummonModal("confirmTrialAction");
                            }}
                            iconProps={{ iconName: "DateTime" }}
                            text={Tr.Translate(
                              "language",
                              "purchase_with_trial"
                            )}
                          />
                        )}
                      </div>
                    )}
                    {!props.onlyPreview && x.TrialDays > 0 && (
                      <div className="subscription-free-trial-hint">
                        {Tr.Translate(
                          "language",
                          "purchase_with_trial_hint"
                        ).replace("$FDAYS", x.TrialDays.toString())}
                      </div>
                    )}
                  </div>
                  <div className="subscription-image">
                    <ImageAssetLoader src={x.SubscriptionImage} />
                  </div>
                  <div className="subscription-type-price-wrap">
                    <div className="subscription-type">
                      {Tr.Translate("subscription", "month")}
                    </div>
                    <div className="subscription-price">{x.ExposedPrice} €</div>
                    <div className="subscription-price-yearly">
                      <div>{Tr.Translate("subscription", "price_yearly")} </div>
                      <div style={{ fontSize: "2em" }}>
                        {x.ExposedPriceYearly} €
                      </div>
                    </div>
                  </div>
                  <div className="subscription-list-box">
                    <SubscriptionFeatureListComponent subscription={x} />
                  </div>
                </div>
              ))}
        </div>
      )}
    </div>
  );
};
export default StorePage;
